"use no memo";

/* eslint-disable complexity, @typescript-eslint/no-shadow, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument */
import { isMarketplacePlatform, isYelpPlatform } from '@local/utils';

import { MARKETPLACE_OBN_CHANNELS } from 'helpers/constants';
import { param } from 'helpers/url';

import { ApiError } from 'types/api';

export const genericErrorMsg =
  'It looks like there are a few errors with your request. ' +
  'Please double check all of your information and try again.';
export const genericDeclineTitle = 'Payment Declined';
export const genericDeclineMsg =
  'Sorry, you have entered invalid information. For your security, please re-enter your card information.';

export const HTTP_STATUS_SERVER_MIN = 500;
export const HTTP_STATUS_SERVER_MAX = 599;

export const UNEXPECTED_ERROR = null; // Could be any number of codes
export const UNAUTHORIZED = '401';
export const NOT_FOUND = '404';
export const TOO_MANY_REQUESTS = '429';
export const INTERNAL_SERVER_ERROR = '500';
export const GATEWAY_TIME_OUT = '504';
export const LENGTH_BELOW_MIN = '10204';
export const INVALID_JSON_SCHEMA = '10205';
export const USER_ACCOUNT_ID_MISMATCH = '20900';
export const NO_ACCOUNT = '20100';
export const PASSWORD_INCORRECT = '20103';
export const GOOGLE_ACCOUNT_INVALID = '20107';
export const GOOGLE_ACCOUNT_INACTIVE = '20108';
export const APPLE_ACCOUNT_INVALID = '20110';
export const APPLE_ACCOUNT_INACTIVE = '20111';
export const CVV_REQUIRED = '20506';
export const INVALID_MISSING_PHONE_NUMBER = '21003';
export const RESTAURANT_NOT_LIVE = '31000';
export const ORDER_LEAD_TIME_EXPIRED = '31001';
export const RESTAURANT_DOES_NOT_ALLOW_TIP = '31050';
export const RESTAURANT_DOES_NOT_ALLOW_SINGLE_USE = '31060';
export const PICKUP_UNAVAILABLE = '32001';
export const BELOW_ORDER_PICKUP_MIN = '32003';
export const ABOVE_ORDER_PICKUP_MAX = '32004';
export const BELOW_PICKUP_MINIMUM_WITH_DISCOUNT = '32005';
export const DELIVERY_UNAVAILABLE = '33001';
export const ADDRESS_OUTSIDE_DELIVERY_AREAS = '33002';
export const BELOW_ORDER_DELIVERY_MIN = '33003';
export const ABOVE_ORDER_DELIVERY_MAX = '33004';
export const TIMESLOT_ORDER_THROTTLED = '33006';
export const BELOW_DELIVERY_MINIMUM_WITH_DISCOUNT = '33005';
export const MENU_EXPIRED = '34001';
export const JSON_REQUIRED_FIELD_MISSING = '10101';
export const ITEM_EXPIRED = '34002';
export const MOD_CATEGORY_EXPIRED = '34003';
export const MOD_EXPIRED = '34004';
export const MODS_INVALID_FOR_ITEM = '34005';
export const INVALID_PROMO_CODE = '35001';
export const BETTER_DEAL_APPLIED = '35002';
export const INVALID_ORDER_TOTAL = '36001';
export const TIP_TOO_BIG = '36003';
export const TIP_AND_TIP_PERCENTAGE_EXCLUSIVE = '36006';
export const PREPTIME_CHANGED = '36008';
export const PREPTIME_REMOVED = '36009';
export const CURBSIDE_CLOSED = '37001';
export const CURBSIDE_INVALID_TABLET_VERSION = '37002';
export const CURBSIDE_MISSING_VEHICLE = '37003';
export const CURBSIDE_MISSING_VEHICLE_COLOR = '37004';
export const CURBSIDE_MISSING_VEHICLE_TYPE = '37005';
export const CURBSIDE_INVALID_VEHICLE_COLOR = '37006';
export const CURBSIDE_INVALID_VEHICLE_TYPE = '37007';
export const CURBSIDE_UNAVAILABLE = '37008';
export const DINE_IN_TABLE_REQUIRED = '38004';
export const DINE_IN_TABLE_NOT_ALLOWED = '38005';
export const MEMBERSHIP_EXISTS = '51114';
export const ADMIN_PRIVILEGES_REQUIRED = '25005';
export const ORDER_ADUSTMENT_EXPIRED = '36010';
export const TIP_ADJUSTMENT_MINIMUM = '36011';
export const TIP_ADJUSTMENT_MAXIMUM = '36012';
export const CARD_DECLINED_GENERIC = '41000';
export const CATERING_NOT_ENABLED = '31010';
export const DUPLICATE_ORDER_SUBMIT = '993';

export const MENU_ID_ERROR_FIELD = 'menu_id';
export const CITY_ERROR_FIELD = 'city';
export const STREET_ERROR_FIELD = 'street_address1';

export const apiErrors = {
  [UNAUTHORIZED]: {
    code: '401',
    name: 'unauthorized',
  },
  [NOT_FOUND]: {
    code: '404',
    name: 'notFound',
  },
  [TOO_MANY_REQUESTS]: {
    code: '429',
    name: 'tooManyRequests',
  },
  [INTERNAL_SERVER_ERROR]: {
    code: '500',
    name: 'internalServerError',
    message: 'Sorry about that. Please go back and try again.',
  },
  [GATEWAY_TIME_OUT]: {
    code: '504',
    name: 'gatewayTimeOut',
    message: 'Gateway Time-out',
  },
  [INVALID_JSON_SCHEMA]: {
    code: '10205',
    name: 'invalidJsonSchema',
    message: genericErrorMsg,
  },
  [PASSWORD_INCORRECT]: {
    code: '20103',
    name: 'passwordIncorrect',
    message: 'Password incorrect',
  },
  [GOOGLE_ACCOUNT_INVALID]: {
    code: '20107',
    message: 'Account creation via Google failed',
  },
  [GOOGLE_ACCOUNT_INACTIVE]: {
    code: '20108',
    message: 'Google account inactive',
  },
  [APPLE_ACCOUNT_INVALID]: {
    code: '20110',
    message: 'Account creation via Apple failed',
  },
  [APPLE_ACCOUNT_INACTIVE]: {
    code: '20111',
    message: 'Apple account inactive',
  },
  [CVV_REQUIRED]: {
    code: '20506',
    name: 'cvvRequired',
    message: 'CVV Required',
  },
  [ORDER_LEAD_TIME_EXPIRED]: {
    code: '31001',
    name: 'orderLeadTimeExpired',
    title: 'Not Enough Time for Advanced Order',
    message:
      'Too much time passed for %@ to accept your order for %@. Please select a new time for your order.',
  },
  [PICKUP_UNAVAILABLE]: {
    code: '32001',
    name: 'pickupUnavailable',
    message: 'Pickup is unavailable at this time for this location.',
  },
  [BELOW_ORDER_PICKUP_MIN]: {
    code: '32003',
    name: 'belowOrderPickupMin',
  },
  [ABOVE_ORDER_PICKUP_MAX]: {
    code: '32004',
    name: 'aboveOrderPickupMax',
  },
  [DELIVERY_UNAVAILABLE]: {
    code: '33001',
    name: 'deliveryUnavailable',
    message: 'Delivery is unavailable at this time for this location.',
  },
  [ADDRESS_OUTSIDE_DELIVERY_AREAS]: {
    code: '33002',
    name: 'addressOutsideDeliveryAreas',
    message: 'This location cannot deliver to your address.',
  },
  [BELOW_ORDER_DELIVERY_MIN]: {
    code: '33003',
    name: 'belowOrderDeliveryMin',
  },
  [ABOVE_ORDER_DELIVERY_MAX]: {
    code: '33004',
    name: 'aboveOrderDeliveryMax',
  },
  [MENU_EXPIRED]: {
    code: '34001',
    name: 'menuExpired',
    message: 'The menu has expired and must be reloaded.',
  },
  [ITEM_EXPIRED]: {
    code: '34002',
    name: 'itemExpired',
  },
  [MOD_CATEGORY_EXPIRED]: {
    code: '34003',
    name: 'modCategoryExpired',
  },
  [MOD_EXPIRED]: {
    code: '34004',
    name: 'modExpired',
  },
  [MODS_INVALID_FOR_ITEM]: {
    code: '34005',
    name: 'modsInvalidForItem',
  },
  [INVALID_ORDER_TOTAL]: {
    code: '36001',
    name: 'invalidOrderTotal',
    message: 'Your order total has been changed to: $%@',
  },
  [TIP_TOO_BIG]: {
    code: '36003',
    name: 'tipTooBig',
  },
  [TIP_AND_TIP_PERCENTAGE_EXCLUSIVE]: {
    code: '36006',
    name: 'tipAndTipPercentageExclusive',
  },
  [MEMBERSHIP_EXISTS]: {
    code: '51114',
    name: 'membershipExists',
    message: 'You are already a member for this location.',
  },
  [ADMIN_PRIVILEGES_REQUIRED]: {
    code: '25005',
    name: 'adminPrivilegesRequired',
  },
  [CATERING_NOT_ENABLED]: {
    code: '31010',
    name: 'cateringNotEnabled',
    message: 'Catering is not enabled for this location.',
  },
};

export const SOCIAL_LOGIN_ERRORS = [
  APPLE_ACCOUNT_INVALID,
  GOOGLE_ACCOUNT_INVALID,
  APPLE_ACCOUNT_INACTIVE,
  GOOGLE_ACCOUNT_INACTIVE,
  JSON_REQUIRED_FIELD_MISSING,
];

// User-correctable order-submit rejections that should not reach Sentry as
// errors. Listing a code here silences it; an unlisted non-5xx code is logged as
// a warning instead (see sendPurchaseFailureEvent), so new codes fail safe.
export const EXPECTED_ORDER_VALIDATION_ERRORS = [
  ORDER_LEAD_TIME_EXPIRED,
  PICKUP_UNAVAILABLE,
  BELOW_ORDER_PICKUP_MIN,
  ABOVE_ORDER_PICKUP_MAX,
  BELOW_PICKUP_MINIMUM_WITH_DISCOUNT,
  DELIVERY_UNAVAILABLE,
  ADDRESS_OUTSIDE_DELIVERY_AREAS,
  BELOW_ORDER_DELIVERY_MIN,
  ABOVE_ORDER_DELIVERY_MAX,
  BELOW_DELIVERY_MINIMUM_WITH_DISCOUNT,
  TIMESLOT_ORDER_THROTTLED,
  MENU_EXPIRED,
  ITEM_EXPIRED,
  MOD_CATEGORY_EXPIRED,
  MOD_EXPIRED,
  MODS_INVALID_FOR_ITEM,
  INVALID_PROMO_CODE,
  BETTER_DEAL_APPLIED,
  PREPTIME_CHANGED,
  PREPTIME_REMOVED,
  DUPLICATE_ORDER_SUBMIT,
];

export const REORDER_WARNINGS = {
  CURBSIDE_UNAVAILABLE: '37008',
  DELIVERY_UNAVAILABLE: '33001',
  MENU_ITEM_UNAVAILABLE: '34006',
};

export const MENU_API_VERSION = '5.0';
export const ORDER_DETAILS_API_VERSION = '6.0';
export const ORDER_SUBMIT_API_VERSION = '6.0';
export const ORDER_VALIDATE_API_VERSION = '6.0';
export const RECENTLY_ORDERED_API_VERSION = '5.0';
export const REORDER_VALIDATE_API_VERSION = '5.0';
export const RESTAURANT_API_VERSION = '5.0';

const CLIENT_TYPES = {
  'Direct-Web': 'direct_web',
};

const packages = {
  marketplaceWeb: 'Marketplace-Web',
  yelpWeb: 'Yelp-Web',
};

function getPackage() {
  if (isMarketplacePlatform()) {
    return packages.marketplaceWeb;
  } else if (isYelpPlatform()) {
    return packages.yelpWeb;
  }

  return process.env.REACT_APP_PACKAGE;
}

export interface ApiRequestFlags {
  marketplaceObnBypass?: boolean;
}

interface ApiRequestReturn extends RequestInit {
  endpoint: string;
}

// Formatting function that returns an object formatted for the Fetch API
export function apiRequest(
  path: string,
  // params needs to be open to accept different data types for different apis
  params?: Record<string, any>,
  method = 'GET', // eslint-disable-line default-param-last
  customHost?: string,
  apiVersion?: string,
  flags?: ApiRequestFlags
): ApiRequestReturn {
  let apiHost =
    process.env.REACT_APP_API_HOST || process.env.NEXT_PUBLIC_API_HOST;
  const reactPackage = getPackage();
  if (process.env.NODE_ENV === 'test') {
    apiHost = 'https://eat.dev.chownow.com/api';
  }
  const endpoint = customHost ? `${customHost}/${path}` : `${apiHost}/${path}`;
  let finalParams = params || {};
  const meta = finalParams.meta || {};
  const version = apiVersion || '3.0';
  const headers = {
    Accept: `application/json version=${version};`,
    'Content-Type': 'application/json; charset=UTF-8',
    'X-CN-App-Info': `${reactPackage}/${
      process.env.REACT_APP_VERSION || process.env.VERSION
    }`,
  };
  // Short circuit any GET requests regardless of domain
  if (method.toUpperCase() === 'GET') {
    return {
      credentials: 'include',
      method,
      headers,
      endpoint: Object.keys(finalParams || {}).length
        ? `${endpoint}?${param(finalParams)}`
        : endpoint,
    };
  }

  // If we are serving the marketplace-themed Order Direct experience, then
  // we want to report Marketplace analytics
  if (isMarketplacePlatform()) {
    const { channel } = meta;
    const shouldBypassStamp =
      flags?.marketplaceObnBypass &&
      channel?.cn_channel &&
      MARKETPLACE_OBN_CHANNELS.includes(channel.cn_channel);
    const marketplaceStamp = shouldBypassStamp
      ? {}
      : {
          marketplace: {
            id: '2',
            x_id: null,
          },
        };
    finalParams = {
      ...finalParams,
      meta: {
        ...meta,
        ...marketplaceStamp,
        channel,
      },
    };

    return {
      credentials: 'include',
      method,
      headers,
      endpoint,
      body: JSON.stringify(finalParams),
    };
  }

  // If we are serving yelp-themed Order Direct experience, we need to read the
  // opportunity token from the payload, and place it within the meta property.
  // x_id is the opporuntity
  if (isYelpPlatform() && finalParams.opportunity_token) {
    finalParams = {
      ...finalParams,
      meta: {
        marketplace: {
          id: '1',
          x_id: params?.opportunity_token,
        },
      },
    };

    // After moving it to the meta object, the opportunity token can be
    // deleted.
    delete finalParams.opportunity_token;

    return {
      credentials: 'include',
      method,
      headers,
      endpoint,
      body: JSON.stringify(finalParams),
    };
  }

  // Catch any Order Direct calls that don't have a client_type key set
  if (
    CLIENT_TYPES[reactPackage as keyof typeof CLIENT_TYPES] &&
    !meta.client_type
  ) {
    finalParams = {
      ...finalParams,
      meta: {
        ...meta,
        client_type: CLIENT_TYPES[reactPackage as keyof typeof CLIENT_TYPES],
      },
    };
    return {
      credentials: 'include',
      method,
      headers,
      endpoint,
      body: JSON.stringify(finalParams),
    };
  }

  return {
    credentials: 'include',
    method,
    headers,
    endpoint,
    body: JSON.stringify(finalParams),
  };
}

interface IdType {
  id?: string;
  restaurantId?: string;
  whenId?: string;
}

export const paths = {
  restaurant: ({ id }: IdType) => `restaurant/${id}`,
  restaurantCatering: ({ id }: IdType) => `restaurant/${id}/catering`,
  restaurantCateringInquiry: ({ id }: IdType) =>
    `restaurant/${id}/catering-inquiry`,
  restaurantMenu: ({ restaurantId }: IdType) =>
    `restaurant/${restaurantId}/menu`,
  restaurantMenuWhen: ({ restaurantId, whenId }: IdType) =>
    `restaurant/${restaurantId}/menu/${whenId}`,
  yelp: {
    restaurant: ({ id }: IdType) => `yelp/${id}/restaurant`,
  },
};

/** returns true if {@link error} is the result of an AbortController abort */
export function isAbortError(error: unknown): boolean {
  return (
    typeof error === 'object' && !!error && 'code' in error && error.code === 20
  );
}

export function getIs5xxError(code: number) {
  return code >= HTTP_STATUS_SERVER_MIN && code <= HTTP_STATUS_SERVER_MAX;
}

export function isApiError(error: unknown): error is ApiError {
  return !!error && typeof error === 'object' && 'message' in error;
}

export function formatError(err: { errors: string }) {
  if (!err) return undefined;

  const responseError = err.errors ? err.errors[0] : { message: err };
  return { errors: [responseError] };
}

interface CommonApiArgs {
  endpoint: string;
  controller?: AbortController;
  customHost?: string;
  apiVersion?: string;
}

interface GetFromApiType extends CommonApiArgs {
  params?: object;
}

// Method for making GET requests to chownow API.
// Properly formats requests for different domains.
export async function getFromChownowApi<T = unknown>({
  endpoint,
  params,
  customHost,
  controller,
  apiVersion,
}: GetFromApiType): Promise<
  { data: T; error: undefined } | { data: undefined; error: unknown }
> {
  const { endpoint: formattedEndpoint, ...options } = apiRequest(
    endpoint,
    params,
    'GET',
    customHost,
    apiVersion
  );
  try {
    const response = await fetch(formattedEndpoint, {
      ...options,
      signal: controller?.signal,
    });
    if (response.status === 404) {
      throw new Error('Error fetching resource');
    }

    const data = await response.json();

    if (response.status === 403) {
      throw data.errors[0] || new Error('Not Authorized');
    }

    return { data, error: undefined };
  } catch (error) {
    return { data: undefined, error };
  }
}

interface PostToApiType extends CommonApiArgs {
  body?: object;
  method?: 'PATCH' | 'POST' | 'PUT' | 'DELETE';
  /** if true, the parsed response data will also be returned with an error */
  // $TSFIXME: the type for this should be automatic { data: T; error: unknown }
  returnDataForErrorResponse?: boolean;
  /** if true, meta property will be removed from the request body */
  skipMeta?: boolean;
  flags?: ApiRequestFlags;
}

// Method for making POST, PUT and DELETE requests to chownow API.
// Properly formats requests for different domains.
export async function postToChownowApi<T = unknown>({
  endpoint,
  returnDataForErrorResponse = false,
  body,
  method,
  customHost,
  apiVersion,
  controller,
  skipMeta = false,
  flags,
}: PostToApiType): Promise<
  { data: T; error: undefined } | { data: undefined; error: unknown }
> {
  const { endpoint: formattedEndpoint, ...options } = apiRequest(
    endpoint,
    body,
    method || 'POST',
    customHost,
    apiVersion,
    flags
  );

  let requestBody = options.body;
  if (skipMeta && requestBody) {
    const bodyObj = JSON.parse(requestBody as string);
    delete bodyObj.meta;
    requestBody = JSON.stringify(bodyObj);
  }

  try {
    const response = await fetch(formattedEndpoint, {
      ...options,
      body: requestBody,
      signal: controller?.signal,
    });
    const { status } = response;
    const data = await response.json();
    switch (status) {
      case 404:
        return {
          data: returnDataForErrorResponse ? data : undefined,
          error: {
            message: 'Error fetching resource',
          },
        };
      case 403:
        return {
          error: data.errors,
          data: returnDataForErrorResponse ? data : undefined,
        };
      case 400:
        // 400 responses do not cause fetch to throw
        // in this case the "data" should be reassigned as an error
        return {
          error: data.errors,
          data: returnDataForErrorResponse ? data : undefined,
        };
      default: {
        if (status >= 400) {
          // any other non-success status
          return {
            error: { code: status },
            data: undefined,
          };
        }

        return {
          error: undefined,
          data,
        };
      }
    }
  } catch (e) {
    return { data: undefined, error: e };
  }
}
